import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useConsent } from "@/contexts/ConsentContext";
import { CONSENT_T, pickConsentLang } from "@/data/consentTranslations";

const LOGO = "/brand/vd-avatar-256.png";
const PHONE = "+33783977370";
const PHONE_DISPLAY = "+33 7 83 97 73 70";
const EMAIL = "nicusor.bocea52@gmail.com";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const { open: openConsent } = useConsent();
  const consentLang = pickConsentLang(i18n?.language || (typeof navigator !== "undefined" ? navigator.language : "fr"));
  const consentLinkLabel = (CONSENT_T[consentLang] || CONSENT_T.fr).footerLink;

  const cities = [
    { to: "/vtc-nice", label: "VTC Nice" },
    { to: "/chauffeur-prive-nice", label: "Chauffeur Privé Nice" },
    { to: "/vtc-aeroport-nice", label: "VTC Aéroport Nice" },
    { to: "/transfert-aeroport-nice", label: "Transfert Aéroport Nice" },
    { to: "/vtc-nice-monaco", label: "VTC Nice → Monaco" },
    { to: "/vtc-nice-cannes", label: "VTC Nice → Cannes" },
    { to: "/vtc-nice-saint-tropez", label: "VTC Nice → Saint-Tropez" },
    { to: "/vtc-monaco", label: "VTC Monaco" },
    { to: "/chauffeur-prive-monaco", label: "Chauffeur Privé Monaco" },
    { to: "/vtc-cannes", label: "VTC Cannes" },
    { to: "/vtc-antibes", label: "VTC Antibes" },
    { to: "/vtc-cote-dazur", label: "VTC Côte d'Azur" },
  ];

  const services = [
    "Transfert Aéroport Nice",
    "Transport Business",
    "Mariages & Événements",
    "Hôtels de luxe",
    "Croisières",
    "Disposition à l'heure",
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#020202] border-t border-white/5 pt-20 pb-24 md:pb-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-grain opacity-30" />
      <div className="container mx-auto px-6 lg:px-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="mb-5">
              <img src={LOGO} alt="VlaDriver VTC Riviera" width="72" height="72" loading="lazy" decoding="async" className="h-16 w-16 rounded-full ring-1 ring-[#D4AF37]/40" />
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-3">
              <a href="#" data-testid="footer-instagram" aria-label="Instagram" className="w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center text-white/60 transition-all">
                <Instagram size={16} />
              </a>
              <a href="#" data-testid="footer-facebook" aria-label="Facebook" className="w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center text-white/60 transition-all">
                <Facebook size={16} />
              </a>
            </div>
          </div>

          {/* Destinations */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">
              {t("footer.cityTitle")}
            </h3>
            <ul className="space-y-2.5">
              {cities.map((c) => (
                <li key={c.to}>
                  <Link to={c.to} className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors">
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">
              {t("footer.serviceTitle")}
            </h3>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s} className="text-sm text-white/70">{s}</li>
              ))}
            </ul>
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mt-7 mb-4">
              {t("footer.blogTitle")}
            </h3>
            <div className="flex flex-col gap-2">
              <Link to="/blog" data-testid="footer-blog-link" className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors">
                {t("nav.blog")} →
              </Link>
              <Link to="/galerie" data-testid="footer-galerie-link" className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors">
                Galerie Côte d&apos;Azur →
              </Link>
              <Link to="/partenaires-hotels" data-testid="footer-partners-link" className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors">
                Partenariat hôtels &amp; concierges →
              </Link>
              <a
                href="https://g.page/r/CWM_ouLn_9JqEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-review-link"
                className="text-sm text-[#D4AF37]/90 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
              >
                ⭐ Laisser un avis Google →
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">
              {t("footer.contactTitle")}
            </h3>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2.5">
                <Phone size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <a href={`tel:${PHONE}`} className="hover:text-[#D4AF37]">{PHONE_DISPLAY}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <a href={`mailto:${EMAIL}`} className="hover:text-[#D4AF37] break-all">{EMAIL}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <span>Nice, Côte d'Azur — France</span>
              </li>
            </ul>
            <div className="mt-6 pt-5 border-t border-white/5">
              <div className="text-xs text-white/40">{t("footer.license")}</div>
              <div className="text-xs text-white/40 mt-1">Bocea Nicusor Vladut</div>
            </div>
          </div>
        </div>

        <div className="gold-divider mb-6" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {year} VlaDriver VTC. {t("footer.rights")}</div>
          <div className="flex flex-wrap gap-5">
            <Link to="/mentions-legales" className="hover:text-[#D4AF37]">{t("footer.legal")}</Link>
            <Link to="/confidentialite" className="hover:text-[#D4AF37]">{t("footer.privacy")}</Link>
            <Link to="/cgv" className="hover:text-[#D4AF37]">{t("footer.cgv")}</Link>
            <button
              type="button"
              onClick={openConsent}
              data-testid="footer-consent-link"
              className="hover:text-[#D4AF37] underline-offset-2 hover:underline"
            >
              {consentLinkLabel}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
